/**
 * Background images follow the login across computers.
 *
 * Images live in this browser's IndexedDB (see mediaStore). This copies them
 * to a private Supabase Storage bucket, under the login's own folder
 * (`backgrounds/<user id>/<image id>`), and downloads ones made elsewhere.
 * The selected background itself is already synced through the settings
 * (it is just an image id), so once the file arrives it shows.
 *
 * Offline-first: failures are skipped and retried on the next run. Deleting an
 * image is remembered so the cloud copy is removed and it is not downloaded
 * again. Nothing here ever deletes a local image on its own.
 */
import { supabase as defaultClient } from './supabase';
import * as defaultMedia from './mediaStore';

export const BUCKET = 'backgrounds';
const OWNER_KEY = 'sleekHearMediaOwner';
const UPLOADED_KEY = 'sleekHearBgUploaded'; // ids this computer has seen in the cloud
const DELETED_KEY = 'sleekHearBgDeleted';   // ids deleted here, cloud copy not yet removed
const ID_PATTERN = /^bg_[a-z0-9]+$/;

const readSet = (storage, key) => {
  try {
    const list = JSON.parse(storage.getItem(key) || '[]');
    return new Set(Array.isArray(list) ? list.filter(x => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
};
const writeSet = (storage, key, set) => {
  try { storage.setItem(key, JSON.stringify([...set])); } catch { /* ignore: it is retried next run */ }
};

/** Remember that an image was deleted here (call before/after deleting it locally). */
export function markImageDeleted(id, storage = window.localStorage) {
  try {
    const deleted = readSet(storage, DELETED_KEY);
    deleted.add(id);
    writeSet(storage, DELETED_KEY, deleted);
  } catch { /* storage unavailable */ }
}

/**
 * One sync pass. Resolves { uploaded, downloaded, removed, failed }; rejects only
 * if the cloud list cannot be read (so a failed read is never mistaken for "empty").
 */
export async function syncBackgrounds({ client = defaultClient, userId, storage = window.localStorage, media = defaultMedia } = {}) {
  const result = { uploaded: 0, downloaded: 0, removed: 0, failed: 0 };
  if (!client || !userId || !media.isMediaStoreSupported()) return result;

  // Images on a shared computer belong to whoever used it last: never upload them into another account.
  const owner = storage.getItem(OWNER_KEY);
  if (owner && owner !== userId) {
    await media.clearImages().catch(() => {});
    storage.removeItem(UPLOADED_KEY);
    storage.removeItem(DELETED_KEY);
  }
  storage.setItem(OWNER_KEY, userId);

  const bucket = client.storage.from(BUCKET);
  const { data: listing, error } = await bucket.list(userId, { limit: 1000 });
  if (error) throw error;
  const remoteIds = new Set((listing || []).map(o => o.name).filter(name => ID_PATTERN.test(name)));

  const uploaded = readSet(storage, UPLOADED_KEY);
  const deleted = readSet(storage, DELETED_KEY);

  // 1. Deletions made here: remove the cloud copy.
  for (const id of [...deleted]) {
    if (!remoteIds.has(id)) { deleted.delete(id); continue; }
    const { error: removeError } = await bucket.remove([`${userId}/${id}`]);
    if (removeError) { result.failed += 1; continue; }
    remoteIds.delete(id);
    deleted.delete(id);
    uploaded.delete(id);
    result.removed += 1;
  }

  // 2. Upload local images the cloud does not have.
  const local = await media.listImages();
  const localIds = new Set(local.map(i => i.id));
  for (const { id } of local) {
    if (remoteIds.has(id)) { uploaded.add(id); continue; }
    if (uploaded.has(id) || deleted.has(id)) continue; // was in the cloud and is gone: deleted on another computer
    try {
      const blob = await media.getImage(id);
      if (!blob) continue;
      const { error: uploadError } = await bucket.upload(`${userId}/${id}`, blob, { contentType: blob.type || 'image/jpeg', upsert: true });
      if (uploadError) throw uploadError;
      uploaded.add(id);
      result.uploaded += 1;
    } catch {
      result.failed += 1;
    }
  }

  // 3. Download images made on other computers.
  for (const id of remoteIds) {
    if (localIds.has(id) || deleted.has(id)) continue;
    try {
      const { data: blob, error: downloadError } = await bucket.download(`${userId}/${id}`);
      if (downloadError || !blob) throw downloadError || new Error('empty');
      await media.putImage({ id, name: 'Background', blob });
      uploaded.add(id);
      result.downloaded += 1;
    } catch {
      result.failed += 1;
    }
  }

  writeSet(storage, UPLOADED_KEY, uploaded);
  writeSet(storage, DELETED_KEY, deleted);
  return result;
}
